import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  getMyNotifications,
  getMyNotificationsFeed,
  getMyUnreadNotificationCount,
  markAllMyNotificationsRead,
  markMyNotificationRead,
} from '@api/notifications';
import { UserNotification } from '@api/notifications/interfaces';

export const myNotificationsQueryKey = ['my-notifications'];

export const useGetMyNotifications = (limit?: number) => {
  return useQuery<UserNotification[]>({
    queryKey: limit
      ? [...myNotificationsQueryKey, 'latest', limit]
      : [...myNotificationsQueryKey, 'all'],
    queryFn: () => getMyNotifications(limit),
  });
};

export const useGetMyNotificationsFeed = (perPage: number) => {
  return useInfiniteQuery({
    queryKey: [...myNotificationsQueryKey, 'feed', perPage],
    queryFn: ({ pageParam }: { pageParam: string | null }) => {
      return getMyNotificationsFeed(perPage, pageParam);
    },
    initialPageParam: null,
    getNextPageParam: (lastPage) => {
      if (!lastPage.meta.hasMore) {
        return undefined;
      }

      return lastPage.meta.nextCursor;
    },
  });
};

export const useGetMyUnreadNotificationCount = () => {
  return useQuery<number>({
    queryKey: [...myNotificationsQueryKey, 'unread-count'],
    queryFn: getMyUnreadNotificationCount,
  });
};

const invalidateNotifications = (
  queryClient: ReturnType<typeof useQueryClient>
) => {
  queryClient.invalidateQueries({
    queryKey: myNotificationsQueryKey,
  });
};

export const useMarkMyNotificationRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markMyNotificationRead,
    onSuccess: () => {
      invalidateNotifications(queryClient);
    },
  });
};

export const useMarkAllMyNotificationsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markAllMyNotificationsRead,
    onSuccess: () => {
      invalidateNotifications(queryClient);
    },
  });
};
